<template>
    <record-editor
        id="firm-editor"
        :record="edited_record"
        :title="page_title"
        :can-view-data="can_view_record_data"
        :can-edit-data="can_edit_record_data"
        :can-delete-data="can_delete_data"
        @remove="confirm_record_delete"
    >
        <div v-if="!!edited_record" slot="right-panel">
            <div v-if="!can_edit_record_data">
                <h1>{{ edited_record.name }}</h1>
                <h3 color="uk-text-muted">{{ edited_record.firm_type }}</h3>
            </div>
            <div v-else class="uk-margin-bottom">
                <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                    <div class="uk-width-1-1">
                        <div class="uk-field-group">
                            <form-input
                                id="name"
                                v-model="edited_record.name"
                                name="name"
                                :error="has_error('name')"
                                :required="true"
                                placeholder="Firm Name"
                                width="1-1"
                                @input="save_field"
                            />
                            <base-button
                                :flavor="aliases.length ? 'normal' : 'link'"
                                title="Aliases"
                                @click.prevent="alias_start_editing"
                            >
                                <open-icon glyph="list" />
                            </base-button>
                        </div>
                    </div>
                    <div class="uk-width-1-1">
                        <form-select-combo
                            id="firm_type"
                            name="firm_type"
                            v-model="edited_record.firm_type"
                            :error="has_error('firm_type')"
                            :required="true"
                            class="uk-width-1-1"
                            @input="save"
                        />
                    </div>
                </div>
            </div>

            <div class="lam-map-container uk-margin-bottom">
                <auto-save-address-form
                    v-model="firm_physical_address"
                    :editable="can_edit_record_data"
                    entity="address"
                    class="uk-width-1-1 uk-margin-bottom"
                />
                <google-map
                    v-if="physical_address&&parseFloat(firm_physical_address.latitude)&&parseFloat(firm_physical_address.longitude)"
                    class="lam-map"
                    :style="{height: '155px'}"
                    :locations="[{name: edited_record.name, location: edited_record.city_state, latitude: firm_physical_address.latitude, longitude: firm_physical_address.longitude}]"
                    :became-visible="became_visible"
                />
            </div>

            <contact-info
                v-if="!!edited_record"
                entity="firm"
                :owner-id="edited_record.id"
                owner-type="App.Firm"
                :editable="can_edit_record_data"
                :show-emails="false"
                :can-add="false"
            />

            <div v-if="is_principal || is_associate_principal || is_administrator || is_developer || is_db_admin_active" class="uk-accordion uk-margin-top">
                <h3 class="uk-accordion-title">
                    Client Notes
                </h3>
                <div class="uk-accordion-content lam-tight">
                    <comments
                        :owner-id="edited_record.id || 0"
                        owner-type="App.Firm"
                    />
                </div>
            </div>

            <div class="uk-accordion uk-margin-top">
                <h3 class="uk-accordion-title">
                    Mailing Address
                </h3>
                <div class="uk-accordion-content lam-tight">
                    <form-checkbox
                        id="distinct_mailing_address"
                        v-model="edited_record.distinct_mailing_address"
                        name="distinct_mailing_address"
                        :editable="can_edit_record_data"
                        :true-value="false"
                        :false-value="true"
                        @input="save"
                    >
                        Same As Physical Address
                    </form-checkbox>
                    <auto-save-address-form
                        v-model="firm_mailing_address"
                        :editable="can_edit_record_data && !!edited_record && !!edited_record.distinct_mailing_address"
                        entity="address"
                        class="uk-margin-top"
                    />
                </div>
            </div>
            <modal-dialog
                id="alias-editor"
                :show.sync="alias_editing"
            >
                <template v-slot:title>
                    {{ edited_record.name }}
                    <div class="uk-text-small">
                        was previously known as
                    </div>
                </template>
                <ul class="uk-list uk-list-striped">
                    <li
                        v-for="alias in aliases"
                        :key="alias.id"
                    >
                        <div class="uk-grid uk-grid-small">
                            <div class="uk-width-9-10">
                                {{ alias.value }}
                            </div>
                            <div class="uk-width-1-10 uk-text-right">
                                <base-button
                                    color="danger"
                                    size="mini"
                                    @click.prevent="alias_remove(alias)"
                                >
                                    <open-icon glyph="trash" />
                                </base-button>
                            </div>
                        </div>
                    </li>
                    <li>
                        <form @submit.prevent.stop class="uk-form uk-grid uk-grid-small">
                            <div class="uk-width-9-10">
                                <form-input
                                    id="new_alias"
                                    name="new_alias"
                                    v-model="new_alias"
                                    placeholder="New Alias"
                                    :editable="true"
                                    :required="false"
                                    :error="has_error('new_alias')"
                                    class="uk-width-1-1"
                                />
                            </div>
                            <div class="uk-width-1-10">
                                <base-button
                                    color="primary"
                                    @click.prevent="alias_add"
                                >
                                    <open-icon glyph="plus" />
                                </base-button>
                            </div>
                        </form>
                    </li>
                </ul>
                <template v-slot:footer>
                    <base-button
                        color="primary"
                        :block="true"
                        @click.native.prevent="alias_stop_editing"
                    >
                        Done
                    </base-button>
                </template>
            </modal-dialog>
        </div>
        <div v-if="!!edited_record" slot="left-panel">
            <div class="uk-accordion">
                <h3 class="uk-accordion-title lam-tight">
                    Contacts
                    <a
                        v-if="can_edit_record_data"
                        href
                        class="uk-icon-button uk-button-mini uk-icon-plus"
                        @click.prevent.stop="add_contact"
                    ></a>
                </h3>
                <div class="uk-accordion-content lam-tight">
                    <contact-data-table
                        id="firm-contacts"
                        context="contact"
                        :context-id="edited_record.id"
                    />
                </div>

                <h3 class="uk-accordion-title lam-tight uk-margin-large-top">
                    Projects
                </h3>
                <div class="uk-accordion-content lam-tight">
                    <project-data-table
                        id="firm-projects"
                        context="firm"
                        :context-id="edited_record.id"
                    />
                </div>

                <h3 class="uk-accordion-title lam-tight uk-margin-large-top">
                    Prospective Projects
                </h3>
                <div class="uk-accordion-content lam-tight">
                    <project-data-table
                        id="firm-prospective-projects"
                        context="firm"
                        :context-id="edited_record.id"
                        :active="false"
                    />
                </div>
            </div>
        </div>
    </record-editor>
</template>

<style lang="less">
    h3 .rating-container {
        font-size: 1em;
        line-height: 1em;
    }
</style>

<script type="text/javascript">
import { mapGetters } from "vuex"

import is_record_editor from "../../mixins/IsRecordEditor"
import has_addresses from "../../mixins/HasAddresses"

import FormInput from "../../ui/FormInput"
import FormSelectCombo from "../../ui/FormSelectCombo"
import FormCheckbox from "../../ui/FormCheckbox"
import BaseButton from "../../ui/BaseButton"
import OpenIcon from "../../ui/OpenIcon"
import ModalDialog from "../../dialogs/ModalDialog/ModalDialog"
import AutoSaveAddressForm from "../../ui/AutoSaveAddressForm"
import GoogleMap from "../../ui/GoogleMap"
import ContactInfo from "../../ui/ContactInfo"
import Comments from "../../ui/Comments"
import ProjectDataTable from "../../ui/DataTable/ProjectDataTable"
import ContactDataTable from "./ContactDataTable"

export default {
    mixins: [is_record_editor, has_addresses],
    components: {
        FormInput,
        FormSelectCombo,
        FormCheckbox,
        BaseButton,
        OpenIcon,
        ModalDialog,
        AutoSaveAddressForm,
        GoogleMap,
        ContactInfo,
        Comments,
        ProjectDataTable,
        ContactDataTable
    },
    watch: {
        physical_address: "refresh_map"
    },
    methods: {
        refresh_map () {
            if(this.physical_address) this.became_visible = true
        },
        add_contact () {
            this.$store.dispatch("CREATE_RECORD", {
                entity: "contact",
                silent: true,
                data: { firm_id: this.edited_record.id }
            }).then(record => {
                this.$router.push({ name: "contact.edit", params: { id: record.id } })
            }).catch(error => {
                this.$error(error.message)
            })
        },
        alias_start_editing () {
            this.new_alias = ""
            this.alias_editing = true
        },
        alias_stop_editing () {
            this.alias_editing = false
        },
        alias_add () {
            if(!this.new_alias) return
            this.$store.dispatch("CREATE_RECORD", {
                entity: "alias",
                data: {
                    topic_type: "App\\Firm",
                    topic_id: this.edited_record.id,
                    field: "name",
                    value: this.new_alias
                }
            }).then(() => {
                this.new_alias = ""
            }).catch(error => {
                if(error) this.$error(error.message)
            })
        },
        alias_remove (alias) {
            this.$store.dispatch("RECORD_DELETE", {
                entity: "alias",
                id: alias.id
            }).catch(error => {
                this.$error(error.message)
            })
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        page_title () {
            if(!this.edited_record || !this.edited_record.name) return (this.edited_record && this.edited_record.id) ? "Firm" : "New Firm"
            return this.edited_record.name
        },
        firm_physical_address () {
            if(!this.edited_record) return {}
            return this.physical_address("App\\Firm", this.edited_record.id)
        },
        firm_mailing_address () {
            if(!this.edited_record) return {}
            return this.mailing_address("App\\Firm", this.edited_record.id, this.edited_record.distinct_mailing_address)
        },
        phone_number () {
            return this.contact_method_of_type("phone")
        },
        url () {
            return this.contact_method_of_type("url")
        },
        aliases () {
            if(!this.edited_record) return []
            return this.entity_records("alias").filter(alias => {
                return (alias.topic_type === "App\\Firm") && (alias.topic_id === this.edited_record.id)
            })
        }
    },
    data () {
        return {
            entity: "firm",
            relation_ids: ["contact_method", "address", "comment", "alias"],
            became_visible: false,

            alias_editing: false,
            new_alias: ""
        }
    }
}
</script>
